import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Autocomplete,
    TextField,
    MenuItem,
    Select,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAllEntries, getEntriesByContentType } from '../../../actions/entryActions';
import { getContentTypes } from '../../../actions/contentActions';

const EntryQuickSwitcher = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { workspaceId, id: currentEntryId } = useParams();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContentTypeId, setSelectedContentTypeId] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    // Track the last entry ID to prevent overwriting user selection when entry data updates re-trigger effect
    const lastEntryIdRef = useRef(null);

    const currentEntry = useSelector((state) => state.entry.entry);
    const contentTypes = useSelector((state) => state.form.contentTypes);

    // Initial setup: Ensure content types are loaded
    useEffect(() => {
        if (workspaceId && (!contentTypes || contentTypes.length === 0)) {
            dispatch(getContentTypes(workspaceId));
        }
    }, [workspaceId, dispatch, contentTypes]);

    // Set selected content type based on current entry, but only when the entry ID actually changes
    useEffect(() => {
        if (currentEntry && currentEntry._id && currentEntry.contentTypeId) {
            if (lastEntryIdRef.current !== currentEntry._id) {
                setSelectedContentTypeId(currentEntry.contentTypeId);
                lastEntryIdRef.current = currentEntry._id;
            }
        }
    }, [currentEntry]);

    // Search logic with debounce
    useEffect(() => {
        let active = true;

        if (searchTerm.length < 3) {
            setOptions([]);
            return undefined;
        }

        setLoading(true);

        const timer = setTimeout(async () => {
            try {
                const params = {
                    identifier: searchTerm,
                    limit: 10
                };

                // Only add contentTypeId if a specific one is selected (not "all")
                if (selectedContentTypeId && selectedContentTypeId !== 'all') {
                    params.contentTypeId = selectedContentTypeId;
                }

                const response = await dispatch(getAllEntries(workspaceId, params));

                if (active) {
                    let newOptions = [];
                    if (response && response.entries) {
                        newOptions = response.entries;
                    }
                    setOptions(newOptions);
                }
            } catch (error) {
                console.error("Failed to search entries", error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }, 800);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [searchTerm, selectedContentTypeId, workspaceId, dispatch]);

    const handleContentTypeChange = (event) => {
        setSelectedContentTypeId(event.target.value);
        setSearchTerm(''); // Clear search when content type changes
        setOptions([]);
    };

    const handleEntrySelect = (event, newValue) => {
        if (newValue) {
            navigate(`/${workspaceId}/edit-entry/${newValue._id}`);
            setSearchTerm('');
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'white', borderRadius: '12px', padding: '4px', border: '1px solid #e2e8f0' }}>
            {/* Content Type Selector */}
            <Select
                value={selectedContentTypeId || 'all'}
                onChange={handleContentTypeChange}
                variant="standard"
                disableUnderline
                displayEmpty
                sx={{
                    minWidth: 120,
                    maxWidth: 150,
                    pl: 1,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    '& .MuiSelect-select': {
                        py: 0.5,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                    }
                }}
            >
                <MenuItem value="all">
                    <em>All Content Types</em>
                </MenuItem>
                {contentTypes.map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                        {type.name}
                    </MenuItem>
                ))}
            </Select>

            {/* Divider */}
            <Box sx={{ width: '1px', height: '20px', bgcolor: 'divider' }} />

            {/* Entry Search */}
            <Autocomplete
                id="entry-quick-search"
                sx={{ width: 250 }}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                getOptionLabel={(option) => option.identifier || ''}
                options={options}
                loading={loading}
                onInputChange={(event, newInputValue, reason) => {
                    // Prevent clearing when selecting an option if desired, but 
                    // standard behavior is fine.
                    if (reason === 'input') {
                        setSearchTerm(newInputValue);
                    }
                }}
                onChange={handleEntrySelect}
                inputValue={searchTerm}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search entries..."
                        variant="standard"
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                        sx={{
                            '& input': {
                                fontSize: '0.875rem',
                                padding: '4px 0 !important'
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                        <li key={key} {...otherProps}>
                            <Box>
                                <Box component="span" sx={{ fontWeight: 'medium', display: 'block' }}>
                                    {option.identifier}
                                </Box>
                                <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {/* Show content type name if searching globally to give context */}
                                    {contentTypes.find(t => t._id === option.contentTypeId)?.name}
                                </Box>
                            </Box>
                        </li>
                    )
                }}
            />
        </Box>
    );
};

export default EntryQuickSwitcher;

